import React, { createContext, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../config';
import { readApiJson } from '../utils/apiResponse';

export const AuthContext = createContext();

let inflightRefresh = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }, []);

  const persistSession = useCallback((accessToken, nextRefresh, userData) => {
    if (accessToken) {
      setToken(accessToken);
      localStorage.setItem('token', accessToken);
    }
    if (nextRefresh) {
      setRefreshToken(nextRefresh);
      localStorage.setItem('refreshToken', nextRefresh);
    }
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const currentRefresh = localStorage.getItem('refreshToken');
    if (!currentRefresh) return null;
    if (inflightRefresh) return inflightRefresh;

    inflightRefresh = (async () => {
      try {
        const res = await fetch(apiUrl('/api/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: currentRefresh }),
        });
        if (!res.ok) return null;
        const data = await readApiJson(res);
        if (!data.token) return null;
        persistSession(data.token, data.refreshToken || currentRefresh);
        return data.token;
      } catch {
        return null;
      } finally {
        inflightRefresh = null;
      }
    })();

    return inflightRefresh;
  }, [persistSession]);

  const fetchCurrentUser = useCallback(async (authToken, { retry = true } = {}) => {
    try {
      const response = await fetch(apiUrl('/api/users/me'), {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await readApiJson(response);
        persistSession(authToken, null, userData);
        return userData;
      }

      if (response.status === 401) {
        if (retry) {
          const nextToken = await refreshAccessToken();
          if (nextToken) return fetchCurrentUser(nextToken, { retry: false });
        }
        clearSession();
        return null;
      }

      return null;
    } catch (error) {
      console.error('AuthContext: Error en fetchCurrentUser:', error);
      return null;
    }
  }, [clearSession, persistSession, refreshAccessToken]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (storedUser && storedToken) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          if (!cancelled) setLoading(false);
          return;
        }
        setToken(storedToken);
        if (storedRefreshToken) setRefreshToken(storedRefreshToken);
        await fetchCurrentUser(storedToken);
      }

      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchCurrentUser]);

  const updateUser = (partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const login = (userData, tokenValue, refreshTokenValue) => {
    persistSession(tokenValue, refreshTokenValue, userData);
    if (tokenValue) fetchCurrentUser(tokenValue, { retry: false });
  };

  const logout = () => {
    clearSession();
  };

  const register = (userData, tokenValue, refreshTokenValue) => {
    login(userData, tokenValue, refreshTokenValue);
  };

  const refreshUser = async () => {
    const current = token || localStorage.getItem('token');
    if (current) await fetchCurrentUser(current);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      refreshToken,
      login,
      logout,
      register,
      loading,
      refreshUser,
      updateUser,
      refreshAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
